import { Injectable, signal } from '@angular/core';

export type FloatingButtonSide = 'start' | 'end';
export type FloatingButtonColor = 'primary' | 'secondary' | 'tertiary' | 'success' | 'warning' | 'danger' | 'light' | 'medium' | 'dark';

export interface FloatingButtonAction {
  id: string;
  icon: string;
  label: string;
  handler: () => void | Promise<void>;
  visible?: () => boolean;
  disabled?: () => boolean;
  color?: FloatingButtonColor;
  cssClass?: string;
  translucent?: boolean;
}

export interface FloatingButtonGroup {
  id: string;
  actions: readonly FloatingButtonAction[];
  priority?: number;
  visible?: () => boolean;
  touchOnly?: boolean;
}

interface RegisteredFloatingButtonGroup extends FloatingButtonGroup {
  token: symbol;
  sequence: number;
}

export interface ActiveFloatingButtonGroup extends FloatingButtonGroup {
  actions: readonly ActiveFloatingButtonAction[];
}

export interface ActiveFloatingButtonAction extends FloatingButtonAction {
  touchOnly?: boolean;
}

@Injectable({ providedIn: 'root' })
export class FloatingButtonSettingsService {
  private static readonly storageKey = 'pc119.floatingButtonSide';
  readonly side = signal<FloatingButtonSide>(this.readSide());
  private readonly registrations = signal<ReadonlyMap<symbol, RegisteredFloatingButtonGroup>>(new Map());
  private readonly suppressions = signal<ReadonlySet<symbol>>(new Set());
  private readonly presentedOverlays = signal<ReadonlySet<object>>(new Set());
  private readonly visibilityRevision = signal(0);
  private registrationSequence = 0;

  setSide(side: FloatingButtonSide): void {
    this.side.set(side);
    try {
      localStorage.setItem(FloatingButtonSettingsService.storageKey, side);
    } catch {
      // 保存領域を使えない場合も、現在の起動中は選択内容を反映する。
    }
  }

  register(group: FloatingButtonGroup): () => void {
    const token = Symbol(group.id);
    const registrations = new Map(this.registrations());
    registrations.set(token, { ...group, token, sequence: ++this.registrationSequence });
    this.registrations.set(registrations);
    return () => {
      const next = new Map(this.registrations());
      next.delete(token);
      this.registrations.set(next);
    };
  }

  suppress(_reason: string): () => void {
    const token = Symbol(_reason);
    this.suppressions.update(current => new Set([...current, token]));
    return () => this.suppressions.update(current => {
      const next = new Set(current);
      next.delete(token);
      return next;
    });
  }

  setOverlayPresented(overlay: object, presented: boolean): void {
    this.presentedOverlays.update(current => {
      const next = new Set(current);
      if (presented) next.add(overlay);
      else next.delete(overlay);
      return next;
    });
  }

  refresh(): void {
    this.visibilityRevision.update(value => value + 1);
  }

  activeGroup(): ActiveFloatingButtonGroup | undefined {
    this.visibilityRevision();
    if (this.suppressions().size || this.presentedOverlays().size) return undefined;
    const groups = [...this.registrations().values()]
      .filter(group => this.isVisible(group.visible))
      .sort((left, right) => (right.priority ?? 0) - (left.priority ?? 0)
        || right.sequence - left.sequence);
    const ids = new Set<string>();
    const icons = new Set<string>();
    const actionGroups = groups.map(group => ({
      group,
      actions: group.actions.filter(action => this.addUniqueVisibleAction(action, ids, icons))
        .map(action => ({ ...action, touchOnly: group.touchOnly })),
    })).filter(candidate => candidate.actions.length);
    const active = actionGroups[0];
    if (!active) return undefined;

    // 優先度が高い（現在の最前面画面の）操作を下段に置き、既存操作を上へ押し上げる。
    const actions = actionGroups.reverse().flatMap(candidate => candidate.actions);
    return { ...active.group, touchOnly: false, actions };
  }

  private addUniqueVisibleAction(action: FloatingButtonAction, ids: Set<string>, icons: Set<string>): boolean {
    if (ids.has(action.id) || icons.has(action.icon) || !this.isVisible(action.visible)) return false;
    ids.add(action.id);
    icons.add(action.icon);
    return true;
  }

  private isVisible(visible?: () => boolean): boolean {
    if (!visible) return true;
    try {
      return visible();
    } catch {
      return false;
    }
  }

  private readSide(): FloatingButtonSide {
    try {
      return localStorage.getItem(FloatingButtonSettingsService.storageKey) === 'start' ? 'start' : 'end';
    } catch {
      return 'end';
    }
  }
}
